/**
 * ChurnAtRiskPage — Phase 34.10.
 *
 * Customers with high or critical churn risk, ordered by score.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';
import { fmtTime, RISK_COLORS } from './CustomersDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface Factor {
  name?: string;
  description?: string;
  contribution?: number;
}

interface Intervention {
  description?: string;
  priority?: string;
}

interface ChurnRiskEntry {
  customer_id: string;
  risk_score: number;
  risk_tier: string;
  factors: Factor[];
  interventions: Intervention[];
  escalated: boolean;
  computed_at: number;
}

interface AtRiskResponse {
  customers: ChurnRiskEntry[];
  total: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

async function fetchAtRisk(): Promise<AtRiskResponse> {
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/churn/at-risk`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<AtRiskResponse>;
}

function useAtRisk() {
  return useQuery({
    queryKey: ['churn', 'at-risk'],
    queryFn: fetchAtRisk,
    refetchInterval: 30_000,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function RiskCard({ c }: { c: ChurnRiskEntry }) {
  const riskColor = RISK_COLORS[c.risk_tier] ?? '#f87171';
  const factors = Array.isArray(c.factors) ? c.factors as Factor[] : [];
  const interventions = Array.isArray(c.interventions) ? c.interventions as Intervention[] : [];

  return (
    <div
      style={{
        background: 'var(--omega-surface, rgba(255,255,255,0.04))',
        border: `1px solid ${riskColor}33`,
        borderLeft: `3px solid ${riskColor}`,
        borderRadius: 6,
        padding: '14px 16px',
        marginBottom: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <span style={{ fontSize: '7.5pt', padding: '1px 8px', border: `1px solid ${riskColor}`, color: riskColor, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3 }}>
          {c.risk_tier}
        </span>
        <Link to={`/omega/customers/${c.customer_id}`} style={{ fontSize: '9.5pt', color: 'var(--omega-ink)', textDecoration: 'none', fontWeight: 600 }}>
          {c.customer_id}
        </Link>
        <span style={{ marginLeft: 'auto', fontSize: '12pt', fontWeight: 700, color: riskColor }}>
          {(c.risk_score * 100).toFixed(0)}%
        </span>
        {c.escalated && (
          <span style={{ fontSize: '7.5pt', padding: '1px 6px', background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.4)', color: '#f87171', borderRadius: 3, fontWeight: 700 }}>
            ESCALATED
          </span>
        )}
      </div>

      {factors.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Risk Factors</div>
          {factors.slice(0, 3).map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', padding: '2px 0' }}>
              <span style={{ flexShrink: 0, color: riskColor }}>·</span>
              <span>{f.description ?? f.name ?? 'Unknown factor'}</span>
            </div>
          ))}
        </div>
      )}

      {interventions.length > 0 && (
        <div>
          <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Suggested Actions</div>
          {interventions.slice(0, 2).map((iv, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, fontSize: '8.5pt', color: '#6ee7b7', padding: '2px 0' }}>
              <span style={{ flexShrink: 0 }}>→</span>
              <span>{iv.description ?? 'No description'}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>
        Computed {fmtTime(c.computed_at)}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ChurnAtRiskPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError } = useAtRisk();
  const customers = data?.customers ?? [];

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Customers', to: '/omega/customers' },
          { label: 'Churn Risk' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Churn Risk
        </h1>
        {customers.length > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              borderRadius: 10,
              background: '#f87171',
              color: '#0a0d12',
              fontSize: '8pt',
              fontWeight: 700,
            }}
          >
            {customers.length}
          </span>
        )}
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load churn data.</div>}
      {!isPending && !isError && customers.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
          No customers at high or critical churn risk.
        </div>
      )}
      {customers.map(c => <RiskCard key={c.customer_id} c={c} />)}
    </div>
  );
}
